import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ClientId} from 'notebook/ELNConstants';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  CreateStructuredTableProps,
  DEFAULT_COLUMN_WIDTH,
  LastSyncedUpstreamValue,
  SchemaAndColumnGroup,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
  TestDefinitionStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  BaseTypedValueType,
  Cell,
  CellIdWithOptionalTable,
  CustomEval,
  CustomEvalResult,
  Table,
  TableColumnType,
  TableDimension,
  TableReorderLocks,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {EMPTY_TYPED_VALUE} from 'notebook/tables/typed-values/DataTypes';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {StringTypedValue, TestDefinitionTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {filterProcedureValidationErrors} from 'procedures/design/validation/procedureValidationUtils';
import {
  InaccessibleProcedureTestDefinition,
  isInaccessibleProcedureTestDefinition,
  ProcedureTemplateCollectionOwner,
  ProcedureTestDefinition,
} from 'procedures/ProcedureDomainTypes';
import {validationErrorsToEvaluatedCellError} from 'procedures/tables/procedureTableAdapterUtils';
import {toTestDefinitionTypedValueFromSearchItem} from 'sample-tests/search/testDefinitionSearchUtils';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import pick from 'util/pick';
import {Optional} from 'util/Types';

import {makeStringCell} from './procedureTableAdapterUtils';
import {TestDefinitionsTableColumn} from './ProcedureTableConstants';

export interface TestDefinitionsTableMetadata {
  procedureTemplateCollectionOwner?: ProcedureTemplateCollectionOwner;
}

export interface TestDefinitionsTableRowMetadata {}

export const ALL_POSSIBLE_COLUMNS_ORDERED = [
  TestDefinitionsTableColumn.TEST,
  TestDefinitionsTableColumn.DESCRIPTION,
  TestDefinitionsTableColumn.RESULT_SCHEMA,
];

function getColumnIndex(column: TestDefinitionsTableColumn): number {
  assert(ALL_POSSIBLE_COLUMNS_ORDERED.includes(column), `Invalid column type: ${column}`);

  return ALL_POSSIBLE_COLUMNS_ORDERED.indexOf(column);
}

const COLUMN_TO_NAME_MAP: {[key in TestDefinitionsTableColumn]: string} = {
  [TestDefinitionsTableColumn.TEST]: 'Test definition',
  [TestDefinitionsTableColumn.DESCRIPTION]: 'Description',
  [TestDefinitionsTableColumn.RESULT_SCHEMA]: 'Result schema',
};

const TEST_DEFINITION_TABLE_COLUMN_WIDTHS = {
  TEST: DEFAULT_COLUMN_WIDTH + 40,
  DESCRIPTION: DEFAULT_COLUMN_WIDTH + 100,
  RESULT_SCHEMA: DEFAULT_COLUMN_WIDTH + 80,
};

export function descriptionColumnResolutionCustomEval(
  descriptionTypedValue: StringTypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
    rowMetadata,
  }: {
    tableMetadata: TestDefinitionsTableMetadata;
    rowMetadata: TestDefinitionsTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const testDefinitionTypedValue = TestDefinitionsTableAdapter.getTestDefinitionFromTypedValue(
    cellDependencies[0]
  );
  if (testDefinitionTypedValue?.id == null) {
    return {val: EMPTY_TYPED_VALUE, error: null, locked: true};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    descriptionTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.TEST_DEFINITION_SUMMARY,
      testDefinitionId: testDefinitionTypedValue.id,
      column: TestDefinitionsTableColumn.DESCRIPTION,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: true};
}

export function resultSchemaColumnResolutionCustomEval(
  resultTypedValue: StringTypedValue,
  columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
    rowMetadata,
  }: {
    tableMetadata: TestDefinitionsTableMetadata;
    rowMetadata: TestDefinitionsTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const testDefinitionTypedValue = TestDefinitionsTableAdapter.getTestDefinitionFromTypedValue(
    cellDependencies[0]
  );
  if (testDefinitionTypedValue?.id == null) {
    return {val: EMPTY_TYPED_VALUE, error: null, locked: true};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    resultTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.TEST_DEFINITION_SUMMARY,
      testDefinitionId: testDefinitionTypedValue.id,
      column: TestDefinitionsTableColumn.RESULT_SCHEMA,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: true};
}

export type TestDefinitionsTable = BaseStructuredTable<
  TestDefinitionsTableMetadata,
  TestDefinitionsTableRowMetadata,
  ClientId
>;

export default class TestDefinitionsTableAdapter extends GenericStructuredTableAdapter<
  TestDefinitionsTableMetadata,
  TestDefinitionsTableRowMetadata
> {
  constructor(options?: StructuredTableAdapterOptions) {
    super(StructuredTableKind.TEST_DEFINITIONS, {allowNoRowsInEntries: true, ...options});
  }

  getTableSpecificCustomEvalFn(
    column: StructuredTableColumn,
    row: StructuredTableRow<TestDefinitionsTableRowMetadata>,
    metadata: TestDefinitionsTableMetadata
  ): CustomEval<{
    tableMetadata: TestDefinitionsTableMetadata;
    rowMetadata: TestDefinitionsTableRowMetadata;
  }> | null {
    if (column.name === COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.DESCRIPTION]) {
      return descriptionColumnResolutionCustomEval;
    } else if (column.name === COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.RESULT_SCHEMA]) {
      return resultSchemaColumnResolutionCustomEval;
    }
    return null;
  }

  getTableSpecificCustomEvalArgs(
    columns: Array<StructuredTableColumn>,
    currentColumnIndex: number,
    currentRowIndex: number,
    tableMetadata: TestDefinitionsTableMetadata,
    toSpreadsheetIndex: (cellId: CellIdWithOptionalTable) => string
  ): Array<string> {
    return [
      `=${toSpreadsheetIndex({row: currentRowIndex, column: getColumnIndex(TestDefinitionsTableColumn.TEST)})}`,
    ];
  }

  getActionFunctions(
    tableMetadata: TestDefinitionsTableMetadata
  ): ActionFunctions<TestDefinitionsTableMetadata, TestDefinitionsTableRowMetadata> {
    return {secondary: [downloadCSVActionFunction]};
  }

  getDisplayName(table: TestDefinitionsTable): string {
    return 'Test definitions';
  }

  getRowDisplayName(_: TestDefinitionsTable): string {
    return 'test definition';
  }

  getBlankRowMetadata(tableMetadata: TestDefinitionsTableMetadata): TestDefinitionsTableRowMetadata {
    return {};
  }

  static getAdapter(): TestDefinitionsTableAdapter {
    return getStructuredTableAdapters()[StructuredTableKind.TEST_DEFINITIONS] as TestDefinitionsTableAdapter;
  }

  allowPasteTable(): boolean {
    return false;
  }

  allowDeleteTable(_: TestDefinitionsTable): {
    canDelete: boolean;
    reason?: string;
  } {
    return {canDelete: false};
  }

  isPositionReadOnly(
    currentColumnIndex: number,
    row: StructuredTableRow<TestDefinitionsTableRowMetadata>,
    structuredTable: TestDefinitionsTable
  ): boolean {
    const currentColumn = this.getColumnsForRow(structuredTable, row)[currentColumnIndex];
    return TestDefinitionsTableAdapter.isColumnReadonOnly(currentColumn);
  }

  skipUnsubmittedIndicatorsForRow(
    structuredTable: TestDefinitionsTable,
    row: StructuredTableRow<TestDefinitionsTableRowMetadata>
  ): boolean {
    return TestDefinitionsTableAdapter.isTestDefinitionEmpty(
      TestDefinitionsTableAdapter.rowToTestDefinition(structuredTable.columns, row)
    );
  }

  _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: TestDefinitionsTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const targetOrganization =
      metadata.procedureTemplateCollectionOwner != null &&
      metadata.procedureTemplateCollectionOwner.type === ModelTypeConstants.ORGANIZATION
        ? pick(metadata.procedureTemplateCollectionOwner, 'id', 'name')
        : undefined;
    const testDefColumn: TestDefinitionStructuredTableColumn = {
      ...makeStructuredTableColumn({
        columnType: TableColumnType.TEST_DEFINITION,
        name: COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.TEST],
        isRequired: true,
        width: TEST_DEFINITION_TABLE_COLUMN_WIDTHS.TEST,
      }),
      columnType: TableColumnType.TEST_DEFINITION,
      targetOrganization,
    };
    return [
      testDefColumn,
      makeStructuredTableColumn({
        columnType: TableColumnType.STRING,
        name: COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.DESCRIPTION],
        isReadOnly: true,
        isRequired: false,
        width: TEST_DEFINITION_TABLE_COLUMN_WIDTHS.DESCRIPTION,
      }),
      makeStructuredTableColumn({
        columnType: TableColumnType.STRING,
        name: COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.RESULT_SCHEMA],
        isReadOnly: true,
        isRequired: false,
        width: TEST_DEFINITION_TABLE_COLUMN_WIDTHS.RESULT_SCHEMA,
      }),
    ];
  }

  static isColumnReadonOnly(column: StructuredTableColumn): boolean {
    return column.name !== COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.TEST];
  }

  static isTestDefinitionEmpty(
    testDefinition: ProcedureTestDefinition | InaccessibleProcedureTestDefinition
  ): boolean {
    return isInaccessibleProcedureTestDefinition(testDefinition) || testDefinition.id == null;
  }

  static makeEmptyTestDefinition(): ProcedureTestDefinition {
    return {
      id: null,
      name: '',
      description: '',
      resultSchemaName: '',
    };
  }

  static makeTestDefinitionCell(
    testDefinition: ProcedureTestDefinition,
    validationErrors: Array<ProcedureValidationError>
  ): Cell {
    const duplicateTestDefinitionCellError = testDefinition.id
      ? validationErrorsToEvaluatedCellError(
          testDefinition.id,
          ProcedureValidationObjectType.TEST_DEFINITION,
          filterProcedureValidationErrors(validationErrors, {
            errorTypes: [ProcedureValidationErrorType.DUPLICATE_TEST_DEFINITION],
          })
        )
      : null;

    if (TestDefinitionsTableAdapter.isTestDefinitionEmpty(testDefinition)) {
      return {
        text: '',
        typedValue: null,
      };
    } else {
      return {
        text: '',
        typedValue: toTestDefinitionTypedValueFromSearchItem(testDefinition),
        error: duplicateTestDefinitionCellError,
        locked: false,
      };
    }
  }

  static makeCellFromTestDefinition(
    testDefinition: ProcedureTestDefinition,
    column: StructuredTableColumn,
    validationErrors: Array<ProcedureValidationError>
  ): Cell {
    switch (column.name) {
      case COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.TEST]:
        return TestDefinitionsTableAdapter.makeTestDefinitionCell(testDefinition, validationErrors);
      case COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.DESCRIPTION]:
        return makeStringCell(testDefinition.description);
      case COLUMN_TO_NAME_MAP[TestDefinitionsTableColumn.RESULT_SCHEMA]:
        return makeStringCell(testDefinition.resultSchemaName);
      default:
        throw new Error(`Unexpected column name: ${column.name}`);
    }
  }

  static testDefinitionToRow(
    testDefinition: ProcedureTestDefinition,
    table: TestDefinitionsTable,
    rowIndex: number,
    validationErrors: Array<ProcedureValidationError>
  ): StructuredTableRow<TestDefinitionsTableRowMetadata> {
    const adapter = TestDefinitionsTableAdapter.getAdapter();

    return {
      ...adapter.getBlankRow(table.metadata, rowIndex, table.columns),
      cells: table.columns.map((column) =>
        TestDefinitionsTableAdapter.makeCellFromTestDefinition(testDefinition, column, validationErrors)
      ),
      metadata: {},
    };
  }

  static rowToTestDefinition(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<TestDefinitionsTableRowMetadata>
  ): ProcedureTestDefinition {
    assert(
      columns.length === row.cells.length,
      'Expected row to have same number of cells as number of columns'
    );
    const testDefinitionTypedValue = TestDefinitionsTableAdapter.getTestDefinitionFromTypedValue(
      row.cells[getColumnIndex(TestDefinitionsTableColumn.TEST)].typedValue
    );

    if (testDefinitionTypedValue == null) {
      return TestDefinitionsTableAdapter.makeEmptyTestDefinition();
    }
    return {
      id: testDefinitionTypedValue.id,
      name: testDefinitionTypedValue.name,
      description: row.cells[getColumnIndex(TestDefinitionsTableColumn.DESCRIPTION)].text,
      resultSchemaName: row.cells[getColumnIndex(TestDefinitionsTableColumn.RESULT_SCHEMA)].text,
    };
  }

  static getTestDefinitionFromTypedValue(
    typedValue: TypedValue | null = null
  ): TestDefinitionTypedValue | null {
    if (typedValue?.type !== BaseTypedValueType.TEST_DEFINITION) {
      return null;
    }
    return typedValue;
  }

  static computeLSUVs(
    rows: Array<StructuredTableRow<TestDefinitionsTableRowMetadata>>,
    columns: Array<StructuredTableColumn>,
    testDefinitions: Array<ProcedureTestDefinition>
  ): Record<string, Array<LastSyncedUpstreamValue>> {
    const lsuvs: Record<string, Array<LastSyncedUpstreamValue>> = {};
    for (let i = 0; i < testDefinitions.length; ++i) {
      const row = rows[i];
      const testDefinition = testDefinitions[i];
      const id = testDefinition.id;
      if (id) {
        lsuvs[id] = TestDefinitionsTableAdapter.makeLastSyncedUpstreamValues(testDefinition, row, columns);
      }
    }
    return lsuvs;
  }

  static makeLastSyncedUpstreamValues(
    this: void,
    testDefinition: ProcedureTestDefinition,
    row: StructuredTableRow<TestDefinitionsTableRowMetadata>,
    columns: Array<StructuredTableColumn>
  ): Array<LastSyncedUpstreamValue> {
    const submittedAt = new Date().toISOString();
    const lsuv = columns
      .filter((column) => !TestDefinitionsTableAdapter.isColumnReadonOnly(column))
      .map((column) => ({
        columnGroup: null,
        columnType: column.columnType,
        columnProvenanceUuid: column.provenanceUuid,
        submittedAt,
        fieldId: null,
        value:
          TestDefinitionsTableAdapter.makeCellFromTestDefinition(testDefinition, column, []).typedValue ??
          null,
      }));
    return lsuv;
  }

  createTableFromTestDefinitions(
    createProps: CreateStructuredTableProps<TestDefinitionsTableMetadata>,
    tableCid: string,
    testDefinitions: Array<ProcedureTestDefinition>,
    procedureValidationErrors: Array<ProcedureValidationError>
  ): TestDefinitionsTable {
    const baseTable = {
      ...this.createTable(createProps),
      cid: tableCid,
    };

    return {
      ...baseTable,
      rows: testDefinitions.map((testDefinition, index) =>
        TestDefinitionsTableAdapter.testDefinitionToRow(
          testDefinition,
          baseTable,
          index,
          procedureValidationErrors
        )
      ),
    };
  }

  toTable(testDefinitionsTable: TestDefinitionsTable): Table {
    const table = super.toTable(testDefinitionsTable);
    return {
      ...table,
      lockedDims: {allowAddRows: true, allowRemoveRows: true, allowEditCols: false},
      allowColumnEdits: false,
    };
  }

  allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }
}
